/**
 * Hook pour gérer la ventilation du budget par catégorie
 * @module hooks/useBudgetVentile
 *
 * Migré vers React Query (useQuery + useMutation)
 */

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateFinancesChantier } from '@/lib/queryInvalidation';
import { useSubscription } from '@/contexts/SubscriptionContext';
import type {
  BudgetVentile,
  BudgetVsReel,
} from '@/types/budgetVentile.types';

// =============================================================================
// Types de retour du hook
// =============================================================================

export interface UseBudgetVentileReturn {
  /** Données de comparaison budget vs réel */
  data: BudgetVsReel | null;
  /** Ventilation actuelle (pour le formulaire) */
  currentVentilation: BudgetVentile | null;
  /** Chargement en cours */
  isLoading: boolean;
  /** Erreur éventuelle */
  error: string | null;
  /** Le chantier a-t-il une ventilation définie */
  hasVentilation: boolean;
  /** Budget total du chantier */
  budgetTotal: number;
  /** Sauvegarder une nouvelle ventilation */
  saveVentilation: (ventilation: BudgetVentile) => Promise<boolean>;
  /** Supprimer la ventilation (revenir au suivi global) */
  clearVentilation: () => Promise<boolean>;
  /** Recharger les données */
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook useBudgetVentile
// =============================================================================

/**
 * Hook pour gérer la ventilation du budget par catégorie pour un chantier
 * @param chantierId - ID du chantier (peut être undefined)
 */
export function useBudgetVentile(chantierId: string | undefined): UseBudgetVentileReturn {
  const { toast } = useToast();
  const { entrepriseId } = useSubscription();
  const queryClient = useQueryClient();

  // -------------------------------------------------------------------------
  // Query : charger les données via RPC get_budget_vs_reel
  // -------------------------------------------------------------------------
  const {
    data: rpcResult,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.finances.budgetVentile(entrepriseId!, chantierId!),
    queryFn: async () => {
      const { data: result, error: fetchError } = await supabase.rpc(
        'get_budget_vs_reel',
        { p_chantier_id: chantierId! }
      );

      if (fetchError) throw fetchError;

      return (result as BudgetVsReel) ?? null;
    },
    enabled: !!entrepriseId && !!chantierId,
  });

  const data = rpcResult ?? null;

  // Extraire la ventilation actuelle pour le formulaire (dérivé de data)
  const currentVentilation: BudgetVentile | null = useMemo(() => {
    if (!data?.has_ventilation || !data?.categories) return null;
    return {
      main_oeuvre: data.categories.main_oeuvre.prevu,
      materiaux: data.categories.materiaux.prevu,
      sous_traitance: data.categories.sous_traitance.prevu,
      frais_annexes: data.categories.frais_annexes.prevu,
    };
  }, [data]);

  // -------------------------------------------------------------------------
  // Mutation : sauvegarder la ventilation
  // -------------------------------------------------------------------------
  const saveVentilationMutation = useMutation({
    mutationFn: async (ventilation: BudgetVentile) => {
      const { error: updateError } = await supabase
        .from('chantiers')
        .update({ budget_ventile: ventilation })
        .eq('id', chantierId!);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      toast({
        title: 'Budget ventilé enregistré',
        description: 'La répartition du budget par catégorie a été sauvegardée.',
      });
      invalidateFinancesChantier(queryClient, entrepriseId!, chantierId!);
    },
    onError: (err) => {
      console.error('Erreur sauvegarde ventilation:', err);
      toast({
        title: 'Erreur',
        description: "Impossible d'enregistrer la ventilation du budget.",
        variant: 'destructive',
      });
    },
  });

  // -------------------------------------------------------------------------
  // Mutation : supprimer la ventilation
  // -------------------------------------------------------------------------
  const clearVentilationMutation = useMutation({
    mutationFn: async () => {
      const { error: updateError } = await supabase
        .from('chantiers')
        .update({ budget_ventile: null })
        .eq('id', chantierId!);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      toast({
        title: 'Ventilation supprimée',
        description: 'Le suivi revient au budget global uniquement.',
      });
      invalidateFinancesChantier(queryClient, entrepriseId!, chantierId!);
    },
    onError: (err) => {
      console.error('Erreur suppression ventilation:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer la ventilation.',
        variant: 'destructive',
      });
    },
  });

  // -------------------------------------------------------------------------
  // Wrappers préservant les signatures de retour (Promise<boolean>)
  // -------------------------------------------------------------------------
  const saveVentilation = async (ventilation: BudgetVentile): Promise<boolean> => {
    if (!chantierId) return false;
    try {
      await saveVentilationMutation.mutateAsync(ventilation);
      return true;
    } catch {
      return false;
    }
  };

  const clearVentilation = async (): Promise<boolean> => {
    if (!chantierId) return false;
    try {
      await clearVentilationMutation.mutateAsync();
      return true;
    } catch {
      return false;
    }
  };

  // -------------------------------------------------------------------------
  // Retour du hook (API publique identique)
  // -------------------------------------------------------------------------
  return {
    data,
    currentVentilation,
    isLoading,
    error: queryError?.message ?? null,
    hasVentilation: data?.has_ventilation ?? false,
    budgetTotal: data?.budget_total ?? 0,
    saveVentilation,
    clearVentilation,
    refetch: async () => { await refetch(); },
  };
}
